import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { productSlug } from '@/utils/slugify';

export default function SearchSuggestions({ className = '' }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const wrapperRef = useRef(null);

  const { data: allProducts = [] } = useQuery({
    queryKey: ['cs-products-search'],
    queryFn: () => base44.entities.Product.list('-created_date', 5000),
    staleTime: 5 * 60 * 1000,
    enabled: shouldFetch,
  });

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const suggestions = useCallback(() => {
    if (!query.trim() || query.length < 2) return [];
    const q = query.toLowerCase();
    return allProducts
      .filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.reference?.toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [query, allProducts])();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?q=${encodeURIComponent(query.trim())}`);
      setQuery('');
      setOpen(false);
    }
  };

  const handleSelect = (product) => {
    navigate(`/product/${productSlug(product)}`);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="flex w-full">
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setShouldFetch(true); }}
          onFocus={() => { setOpen(true); setShouldFetch(true); }}
          type="text"
          placeholder="Rechercher un produit, une marque..."
          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-l-md text-sm focus:outline-none focus:border-cs-blue"
        />
        {query && (
          <button type="button" onClick={() => { setQuery(''); setOpen(false); }}
            className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <button type="submit" aria-label="Rechercher" className="bg-cs-blue text-white px-4 rounded-r-md hover:bg-cs-blue-dark transition-colors">
          <Search className="w-4 h-4" />
        </button>
      </form>

      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-xl z-50 max-h-80 overflow-y-auto">
          {suggestions.map(p => (
            <button
              key={p.id}
              onClick={() => handleSelect(p)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
            >
              <div className="w-10 h-10 shrink-0 bg-gray-100 rounded overflow-hidden">
                {(p.thumbnail_url || p.image_url) && <img src={p.thumbnail_url || p.image_url} alt="" className="w-full h-full object-contain p-1" loading="lazy" decoding="async" />}
              </div>
              <div className="min-w-0">
                {p.brand && <p className="text-[10px] font-bold text-cs-blue uppercase tracking-wide">{p.brand}</p>}
                <p className="text-sm text-gray-800 truncate font-medium">{p.name}</p>
                <p className="text-xs text-gray-500">{p.price?.toFixed(2)} €</p>
              </div>
            </button>
          ))}
          <button
            onClick={handleSubmit}
            className="w-full px-3 py-2.5 text-sm text-cs-blue font-semibold text-center hover:bg-cs-blue/5 transition-colors"
          >
            Voir tous les résultats pour "{query}" →
          </button>
        </div>
      )}
    </div>
  );
}